import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { router } from '@inertiajs/react';
import { MdLock, MdLockOpen, MdLockOutline, MdSync } from 'react-icons/md';
import { apiDelete, apiGet, apiPost } from '@/api/client';
import { useNoteContext } from '@/components/notes/context/NoteContext';
import { triggerErrorMessage, triggerInfoMessage } from '@/components/ui/toast';
import ConfirmDialog from '@/components/shared/ConfirmDialog';
import type { ApiNote } from '@/api/notes/types';
import { openNoteBody } from '@/services/NoteBodyService';
import { encryptNoteBody, generateNoteKey, initSodium, wrapNoteKey } from '@/services/CryptoService';
import { resolveNoteKey } from '@/services/NoteKeyResolver';
import { useMasterPasswordStore } from '@/store/masterPasswordStore';
import { useNoteKeyStore } from '@/store/noteKeyStore';

type Revision = { id: number; seq: number };
type ConversionItem = { id: number; seq: number };
type Progress = { done: number; total: number; phase: 'converting' | 'saving' };

const NoteEncryptionControl: React.FC = () => {
  const {
    note, noteId, currentContent, saveCurrent, onEncryptionComplete,
    hasKeyWrapper, setHasKeyWrapper, isReadOnly,
    isEncryptionConversionActive, setEncryptionConversionActive,
  } = useNoteContext();
  const [progress, setProgress] = useState<Progress | null>(null);
  const [running, setRunning] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [targetEncrypted, setTargetEncrypted] = useState<boolean | null>(null);

  useEffect(() => {
    if (!isEncryptionConversionActive) return;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    const removeInertia = router.on('before', (event) => {
      event.preventDefault();
      triggerInfoMessage('Wait for encryption conversion to finish before leaving this note.');
    });
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
      removeInertia();
    };
  }, [isEncryptionConversionActive]);

  const startConversion = () => {
    if (!noteId || !note) return;
    const targetMode = !Boolean(note.encrypted);
    // Read the key at invocation time. The unlock callback retries this action
    // using a closure created before the key was added to the store.
    if (!useMasterPasswordStore.getState().masterKey) {
      requestMasterKey(() => { void startConversion(); });
      return;
    }

    setTargetEncrypted(targetMode);
    setConfirmationOpen(true);
  };

  const confirmConversion = async () => {
    if (!noteId || !note || targetEncrypted === null) return;
    setConfirmationOpen(false);
    const convertingToEncrypted = targetEncrypted;
    const conversionId = crypto.randomUUID();
    let conversionCommitted = false;

    try {
      if (!(await saveCurrent())) throw new Error('Save the current edits before changing encryption.');
      setRunning(true);
      setEncryptionConversionActive(true);
      await initSodium();
      const wrapper = convertingToEncrypted && !hasKeyWrapper ? await createWrapperPayload(noteId) : undefined;
      const noteKeyResult = await resolveNoteKey(noteId);
      if (!noteKeyResult.ok) throw new Error('Could not unlock the note key. Check your master password and retry.');
      const revisions = await listAllRevisions(noteId);
      if (revisions.length === 0) throw new Error('This note has no body revisions to convert.');

      const sourceEncrypted = Boolean(note.encrypted);
      const convertedItems: ConversionItem[] = [];
      setProgress({ done: 0, total: revisions.length, phase: 'converting' });
      for (const revision of revisions) {
        const response = await apiGet<{ note: ApiNote }>(`/app/v1/note/${noteId}?content_id=${revision.id}`);
        if (response.note.encrypted !== sourceEncrypted || response.note.seq !== revision.seq) {
          throw new Error('The note changed while conversion was running. Refresh and retry.');
        }
        const plaintext = sourceEncrypted ? await openNoteBody(noteId, response.note.content) : response.note.content;
        const content = convertingToEncrypted
          ? encryptNoteBody(plaintext, noteId, noteKeyResult.noteKey)
          : plaintext;
        await apiPost(`/app/v1/note/${noteId}/encryption/revisions`, {
          conversion_id: conversionId,
          target_encrypted: convertingToEncrypted,
          id: revision.id,
          seq: revision.seq,
          content,
        });
        convertedItems.push({ id: revision.id, seq: revision.seq });
        setProgress({ done: convertedItems.length, total: revisions.length, phase: 'converting' });
      }

      setProgress({ done: revisions.length, total: revisions.length, phase: 'saving' });
      const result = await apiPost<{ note: ApiNote; seq: number }>(`/app/v1/note/${noteId}/encryption/commit`, {
        conversion_id: conversionId,
        target_encrypted: convertingToEncrypted,
        items: convertedItems,
        ...(wrapper ? { key_wrapper: wrapper } : {}),
      });
      conversionCommitted = true;
      onEncryptionComplete(result.note.encrypted ?? convertingToEncrypted, result.seq, currentContent);
      if (convertingToEncrypted) setHasKeyWrapper(true);
      triggerInfoMessage(convertingToEncrypted ? 'Note body and history are now encrypted.' : 'Note body and history are now unencrypted.');
    } catch (error: any) {
      triggerErrorMessage(error?.message || 'Encryption conversion failed. Your note remains in its previous mode.');
    } finally {
      if (!conversionCommitted) {
        await apiDelete(`/app/v1/note/${noteId}/encryption/staging/${conversionId}`).catch(() => undefined);
      }
      setRunning(false);
      setProgress(null);
      setEncryptionConversionActive(false);
    }
  };

  if (!note) return null;
  const encrypted = Boolean(note.encrypted);
  const disabled = isReadOnly || running;
  const progressPercent = progress?.total
    ? progress.phase === 'saving' ? 95 : Math.round((progress.done / progress.total) * 90)
    : 0;

  return (
    <>
      <div className="relative flex items-center gap-1">
        <button
          type="button"
          disabled={disabled}
          onClick={startConversion}
          className="rounded p-1.5 text-gray-600 hover:bg-gray-100 disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-700"
          title={running ? 'Converting note body and history' : encrypted ? (note.body_locked ? 'Encrypted and locked' : 'Encrypted and unlocked') : 'Unencrypted note body'}
          aria-label={running ? 'Converting encryption' : encrypted ? 'Disable note encryption' : 'Enable note encryption'}
        >
          {running ? <MdSync className="animate-spin" /> : encrypted ? (note.body_locked ? <MdLock /> : <MdLockOpen />) : <MdLockOutline />}
        </button>
        {progress && <span className="text-xs whitespace-nowrap" role="status">{progress.done}/{progress.total}</span>}
      </div>
      <ConfirmDialog
        isOpen={confirmationOpen}
        type={targetEncrypted ? 'warning' : 'danger'}
        title={targetEncrypted ? 'Encrypt this note?' : 'Disable note encryption?'}
        cancelText="Cancel"
        confirmText={targetEncrypted ? 'Encrypt note' : 'Disable encryption'}
        shouldCloseOnOverlayClick
        shouldCloseOnEsc
        onClose={() => setConfirmationOpen(false)}
        onRequestClose={() => setConfirmationOpen(false)}
        onCancel={() => setConfirmationOpen(false)}
        onConfirm={() => void confirmConversion()}
      >
        <div className="space-y-3 text-gray-600 dark:text-gray-300">
          {targetEncrypted ? (
            <p>The note body will be encrypted on this device before it is sent to the server.</p>
          ) : (
            <p>The note body will be sent to the server without application-level encryption. HTTPS remains enabled.</p>
          )}
          {note.has_history && <p>The current body and all history, including any draft, will also be converted.</p>}
          <p>Attachments remain unencrypted.</p>
        </div>
      </ConfirmDialog>
      {running && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40" role="alertdialog" aria-modal="true" aria-label="Converting note encryption">
          <div className="w-[min(90vw,28rem)] rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
            <h2 className="text-lg font-semibold">Converting note body</h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              {progress?.phase === 'saving'
                ? 'Activating all converted revisions atomically. Keep this page open.'
                : 'Converting and uploading revisions one at a time. The editor and navigation are locked until conversion finishes.'}
            </p>
            <div className="mt-4 h-2 overflow-hidden rounded bg-gray-200 dark:bg-gray-700">
              <div className="h-full bg-blue-600 transition-all" style={{ width: `${progressPercent}%` }} />
            </div>
            <p className="mt-2 text-sm" role="status">
              {progress?.phase === 'saving' ? 'Activating converted revisions…' : `${progress?.done ?? 0} of ${progress?.total ?? 0} revisions converted and uploaded`}
            </p>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

async function createWrapperPayload(noteId: string) {
  const existing = await resolveNoteKey(noteId);
  const masterKey = useMasterPasswordStore.getState().masterKey;
  if (!masterKey) throw new Error('Unlock your master password first.');
  if (existing.ok) return wrapNoteKey(existing.noteKey, masterKey);
  if (existing.reason !== 'no-wrapper') throw new Error('Could not load the existing note key. Check your master password and retry.');
  const key = generateNoteKey();
  useNoteKeyStore.getState().setNoteKey(noteId, key);
  return wrapNoteKey(key, masterKey);
}

function requestMasterKey(retry: () => void) {
  const master = useMasterPasswordStore.getState();
  if (master.isConfigured) master.openUnlockModal(retry);
  else master.openModal(retry);
}

async function listAllRevisions(noteId: string): Promise<Revision[]> {
  const first = await apiGet<{ data: Revision[]; has_more: boolean }>(`/app/v1/note/${noteId}/versions`);
  const revisions = [...first.data];
  let cursor = revisions.length ? Math.min(...revisions.map((row) => row.id)) : 0;
  let hasMore = first.has_more;
  while (hasMore) {
    const page = await apiGet<{ data: Revision[]; has_more: boolean }>(
      `/app/v1/note/${noteId}/versions?before_id=${cursor}`,
    );
    revisions.push(...page.data);
    hasMore = page.has_more;
    if (page.data.length) cursor = Math.min(...page.data.map((row) => row.id));
    else hasMore = false;
  }
  return revisions.sort((a, b) => a.id - b.id);
}

export default NoteEncryptionControl;
